import React from 'react';
import Container from './ui/Container';
import PageSEO from './common/PageSEO';
import { useT, useLang } from '../i18n';

const T = {
  es: {
    seo: {
      title: 'Sobre Cadenia - Nuestra Historia',
      description: 'Fundada en 2022, Cadenia transforma la gestión de la Cadena de Suministro con soluciones de visibilidad, inteligencia y documentación digital para empresas en Europa.',
      keywords: 'cadenia empresa, sobre cadenia, historia cadenia, equipo cadenia, logística, supply chain españa',
    },
    heroTitle: 'Dedicados a nuestros clientes',
    heroText: 'En Cadenia transformamos la gestión de la Cadena de Suministro, ofreciendo soluciones innovadoras y personalizadas que marcan la diferencia.',
    timeline: [
      {
        year: '2022',
        title: 'Los inicios',
        text: 'En febrero de 2022, Cadenia se funda en Madrid. El equipo fundador aportaba experiencia en el transporte, el desarrollo de software empresarial, la trazabilidad y las telecomunicaciones. Comenzamos nuestras operaciones con el seguimiento en tiempo real para Sika España, líder mundial en adhesivos y productos de construcción.'
      },
      {
        year: '2023',
        title: 'Primer Proyecto Internacional',
        text: 'Nuestra capacidad y compromiso se consolidan con el grupo Lecta, referente en el sector papelero en Europa. Desarrollamos una solución personalizada implantada en España, Francia, Italia y Portugal, que aporta trazabilidad y análisis de datos avanzados al grupo.'
      },
      {
        year: '2024',
        title: 'Evolución y Nuevos Desafíos',
        text: 'La plataforma se enriquece con nuevas funcionalidades, como el análisis avanzado de datos, la incorporación del eCMR y la digitalización de documentos de transporte, reforzando nuestra posición como referente en el sector.'
      },
      {
        year: '2025',
        title: 'El cambio a Cadenia',
        text: 'La integración de IA en nuestros productos transforma datos en decisiones estratégicas, optimizando operaciones y elevando el nivel de personalización de nuestras soluciones. El cambio de marca refleja la evolución del enfoque del dato hacia la IA.'
      }
    ],
    howTitle: 'Cómo trabajamos',
    howSubtitle: 'Enfocados en el éxito del cliente',
    how: [
      {
        title: 'Personalizaciones',
        text: 'Ajustamos cada solución a las necesidades específicas de nuestros clientes, garantizando máxima eficiencia.'
      },
      {
        title: 'Integraciones',
        text: 'Procesamos múltiples orígenes de datos en paralelo, ofreciendo una visión unificada y flexible de la cadena de suministro.'
      },
      {
        title: 'Inteligencia',
        text: 'Los datos y la Inteligencia Artificial están en el centro de nuestras soluciones. Cada funcionalidad se diseña para generar valor a partir de la información.'
      },
      {
        title: 'Seguro y escalable',
        text: 'Infraestructura 100% serverless en AWS Europa (París y Frankfurt). Escalado automático, alta disponibilidad y cumplimiento GDPR.'
      }
    ],
    ctaTitle: '¿Preparado para dar el siguiente paso?',
    ctaText: 'Cuéntenos su operativa y le mostraremos cómo Cadenia puede ayudarle. Sin compromiso.',
    ctaButton: 'Solicitar Demo',
  },
  en: {
    seo: {
      title: 'About Cadenia - Our Story',
      description: 'Founded in 2022, Cadenia transforms supply chain management with visibility, intelligence and digital documentation solutions for companies across Europe.',
      keywords: 'cadenia company, about cadenia, cadenia story, cadenia team, logistics, supply chain spain',
    },
    heroTitle: 'Dedicated to our customers',
    heroText: 'At Cadenia we transform supply chain management, delivering innovative, tailored solutions that make a difference.',
    timeline: [
      {
        year: '2022',
        title: 'The beginning',
        text: 'In February 2022, Cadenia was founded in Madrid. The founding team brought experience in transportation, enterprise software development, traceability and telecommunications. We began operations providing real-time tracking for Sika Spain, a global leader in adhesives and construction products.'
      },
      {
        year: '2023',
        title: 'First International Project',
        text: 'Our capabilities and commitment were consolidated with the Lecta group, a leading player in the European paper industry. We built a tailored solution deployed across Spain, France, Italy and Portugal, bringing traceability and advanced data analytics to the group.'
      },
      {
        year: '2024',
        title: 'Evolution and New Challenges',
        text: 'The platform grew with new capabilities such as advanced data analytics, the addition of eCMR and the digitalization of transport documents, strengthening our position as a reference in the sector.'
      },
      {
        year: '2025',
        title: 'The change to Cadenia',
        text: 'Integrating AI into our products turns data into strategic decisions, optimizing operations and raising the level of personalization of our solutions. The rebrand reflects the evolution from a data-driven approach towards AI.'
      }
    ],
    howTitle: 'How we work',
    howSubtitle: 'Focused on customer success',
    how: [
      {
        title: 'Customization',
        text: 'We tailor every solution to our customers\' specific needs, ensuring maximum efficiency.'
      },
      {
        title: 'Integrations',
        text: 'We process multiple data sources in parallel, delivering a unified, flexible view of the supply chain.'
      },
      {
        title: 'Intelligence',
        text: 'Data and Artificial Intelligence sit at the core of our solutions. Every feature is designed to generate value from information.'
      },
      {
        title: 'Secure and scalable',
        text: '100% serverless infrastructure on AWS Europe (Paris and Frankfurt). Auto-scaling, high availability and GDPR compliance.'
      }
    ],
    ctaTitle: 'Ready to take the next step?',
    ctaText: 'Tell us about your operations and we will show you how Cadenia can help. No commitment.',
    ctaButton: 'Request a Demo',
  },
};

const About = () => {
  const t = useT(T);
  const { path } = useLang();

  return (
    <div className="pt-32">
      <PageSEO
        title={t.seo.title}
        description={t.seo.description}
        keywords={t.seo.keywords}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-24">
        <Container>
          <div className="text-center">
            <h1 className="text-5xl font-light text-slate-900 mb-8">
              {t.heroTitle}
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
              {t.heroText}
            </p>
          </div>
        </Container>
      </section>

      {/* Historia — Timeline */}
      <section className="py-24 bg-white">
        <Container>
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-blue-200 via-slate-200 to-slate-100 hidden md:block"></div>

              <div className="space-y-16">
                {t.timeline.map((item, i) => (
                  <div key={i} className="relative flex gap-8 md:gap-12 group">
                    {/* Year marker */}
                    <div className="flex-shrink-0 w-14 relative">
                      <div className="w-14 h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center shadow-sm group-hover:border-blue-300 group-hover:shadow-md transition-all duration-300 relative z-10">
                        <span className="text-sm font-mono font-medium text-slate-500 group-hover:text-blue-600 transition-colors">{item.year}</span>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex-1 pb-2">
                      <h2 className="text-2xl md:text-3xl font-light text-slate-900 mb-4 group-hover:text-blue-900 transition-colors">{item.title}</h2>
                      <p className="text-base md:text-lg text-slate-600 leading-relaxed">{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Cómo trabajamos */}
      <section className="py-24 bg-slate-50">
        <Container>
          <div className="text-center mb-20">
            <h2 className="text-4xl font-light text-slate-900 mb-6">{t.howTitle}</h2>
            <p className="text-xl text-slate-600">{t.howSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.how[0].title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                {t.how[0].text}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.how[1].title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                {t.how[1].text}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.how[2].title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                {t.how[2].text}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 mb-6 mx-auto">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-medium text-slate-900 mb-4">{t.how[3].title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">
                {t.how[3].text}
              </p>
            </div>
          </div>
        </Container>
      </section>



      {/* CTA */}
      <section className="py-24 bg-white">
        <Container>
          <div className="text-center">
            <h2 className="text-4xl font-light text-slate-900 mb-8">
              {t.ctaTitle}
            </h2>
            <p className="text-lg text-slate-600 mb-12 leading-relaxed max-w-2xl mx-auto">
              {t.ctaText}
            </p>
            <a
              href={`${path('home')}#contacto`}
              className="inline-block bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-12 py-4 text-sm font-medium uppercase tracking-wide transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {t.ctaButton}
            </a>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default About;